import type React from 'react';
import {
  ADDON_KEYS,
  ADDON_META,
  type AddonKey,
  type AddonSelection,
  type PlanPricing,
  formatMinutes,
  formatMoney,
  formatRate,
} from '../../shared/plan';
import type { ReceiptLine, Tag, Tone } from '../../ui/v2';

/**
 * Billing V2 vocabulary: API shapes for `/api/billing/*` (store and group
 * scope) and the helpers that turn them into words. Field names are the
 * API's.
 */

export type SubscriptionStatus =
  | 'ACTIVE'
  | 'TRIALING'
  | 'PAST_DUE'
  | 'CANCELED'
  | 'PENDING';

/** `StoreSubscriptionSerializer` — used for store and group subscriptions. */
export interface Subscription {
  status: SubscriptionStatus;
  currency: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  provider?: string | null;
  is_subscription_started: boolean;
  /** `plan` when the catalog price applies, `custom` when an admin override does. */
  pricing_source?: 'plan' | 'custom';
  plan: { code: string; name: string } | null;
  base_price_cents: number;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  setup_fee_cents?: number;
  per_order_rate_cents?: number;
  multilingual_addon_cents?: number;
  multilingual_included?: boolean;
  sms_ordering_addon_cents?: number;
  sms_ordering_included?: boolean;
  opentable_resy_addon_cents?: number;
  opentable_resy_included?: boolean;
  multilingual_addon_active?: boolean;
  sms_ordering_addon_active?: boolean;
  opentable_resy_addon_active?: boolean;
  pending_plan?: { code: string; name: string } | null;
  pending_base_price_cents?: number | null;
}

/** `GET /api/billing/usage-summary/?period_start=` */
export interface UsageSummary {
  store_id?: number;
  period_start?: string;
  period_end?: string;
  is_current_period?: boolean;
  store_timezone: string;
  currency: string;
  plan_code?: string | null;
  plan_name?: string | null;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  base_price_cents: number;
  minutes_used: number;
  overage_minutes: number;
  overage_cost_cents: number;
  projected_total_cents: number;
  bucket?: {
    quantity_ms: number;
    period_start: string | null;
    period_end: string | null;
    finalized_at: string | null;
  } | null;
  subscription: Subscription | null;
  billing_managed_at_group_level?: boolean;
  store_group_id?: number | null;
  group_plan_code?: string | null;
  group_plan_name?: string | null;
  group_included_minutes?: number | null;
}

/** `GET /api/billing/periods/` (newest first) */
export interface BillingPeriod {
  period_start: string;
  period_end: string;
  is_current: boolean;
  finalized: boolean;
  invoice_id: number | null;
}

export type InvoiceStatus =
  | 'PAID'
  | 'OPEN'
  | 'DRAFT'
  | 'VOID'
  | 'UNCOLLECTIBLE';

/** `GET /api/billing/invoices/?page=&page_size=` */
export interface Invoice {
  id: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  status: InvoiceStatus;
  total_cents: number;
  hosted_invoice_url: string;
  created_at: string;
}

export interface InvoicePage {
  count: number;
  results: Invoice[];
}

/** `GET /api/billing/usage-summary/daily/?start_date=&end_date=` */
export interface DailyUsage {
  date: string;
  minutes_used: number;
}

/** `GET /api/billing/plans/` (active, sorted by display order) */
export interface Plan {
  code: string;
  name: string;
  description?: string;
  currency: string;
  base_price_cents: number;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  setup_fee_cents?: number;
  per_order_rate_cents?: number;
  multilingual_addon_cents?: number;
  multilingual_included?: boolean;
  sms_ordering_addon_cents?: number;
  sms_ordering_included?: boolean;
  opentable_resy_addon_cents?: number;
  opentable_resy_included?: boolean;
  feature_points?: string[];
  trial_period_days?: number;
}

/** `GET …/subscription/preview-change/?plan_code=` */
export interface PreviewChange {
  is_upgrade: boolean;
  subtotal_cents: number;
  adjustments_cents: number;
  total_cents: number;
  next_payment_date: string | null;
  payment_method: { brand: string; last4: string } | null;
}

export interface GroupStoreRow {
  store_id: number;
  store_name: string;
  minutes_used: number;
  share_percent?: number;
  allocated_overage_cents: number;
}

/** `GET /api/billing/group/{id}/usage-summary/?period_start=` */
export interface GroupUsageSummary {
  group_id: number;
  group_name: string;
  billing_timezone?: string;
  currency: string;
  plan_code?: string | null;
  plan_name?: string | null;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  base_price_cents: number;
  addon_cost_cents: number;
  total_minutes_used: number;
  overage_minutes: number;
  overage_cost_cents: number;
  projected_total_cents: number;
  /** True once the period is invoiced and the per-store split is final. */
  allocations_frozen?: boolean;
  stores: GroupStoreRow[];
}

// ---------------------------------------------------------------------------
// Words for states
// ---------------------------------------------------------------------------

export interface StatusWord {
  label: string;
  tone: Tone;
  hollow?: boolean;
}

/** True when Stripe bills this subscription now (plan changes are in place). */
export function isLive(sub: Subscription | null | undefined): boolean {
  return (
    !!sub &&
    sub.is_subscription_started &&
    (sub.status === 'ACTIVE' ||
      sub.status === 'TRIALING' ||
      sub.status === 'PAST_DUE')
  );
}

/**
 * The activation fee is already paid and a local plan exists, but Stripe has
 * not started billing yet (`is_subscription_started` is false). Posting a new
 * checkout would charge the setup fee and the first month again. PENDING is
 * not this state: that row is custom pricing still waiting for its first
 * checkout.
 */
export function isAwaitingBillingStart(
  sub: Subscription | null | undefined,
): boolean {
  return (
    !!sub &&
    !sub.is_subscription_started &&
    (sub.status === 'ACTIVE' ||
      sub.status === 'TRIALING' ||
      sub.status === 'PAST_DUE')
  );
}

export function isNotStarted(sub: Subscription | null | undefined): boolean {
  return !!sub && sub.status !== 'CANCELED' && !sub.is_subscription_started;
}

/**
 * Subscription status → dot + word. `cancel_at_period_end` wins over ACTIVE
 * because "Ends Oct 1" is the fact the owner needs.
 */
export function subscriptionWord(
  sub: Subscription | null,
  tz?: string,
): StatusWord {
  if (!sub) return { label: 'No plan', tone: 'neutral', hollow: true };
  if (sub.status === 'CANCELED') return { label: 'Cancelled', tone: 'neutral' };
  if (sub.status === 'PENDING' || !sub.is_subscription_started)
    return { label: 'Not started', tone: 'warning', hollow: true };
  if (sub.status === 'PAST_DUE') return { label: 'Past due', tone: 'danger' };
  if (sub.cancel_at_period_end)
    return {
      label: sub.current_period_end
        ? `Ends ${formatDate(sub.current_period_end, tz)}`
        : 'Ends this period',
      tone: 'warning',
      hollow: true,
    };
  if (sub.status === 'TRIALING')
    return { label: 'Trial', tone: 'accent', hollow: true };
  return { label: 'Active', tone: 'success' };
}

export const INVOICE_WORD: Record<InvoiceStatus, StatusWord> = {
  PAID: { label: 'Paid', tone: 'success' },
  OPEN: { label: 'Open', tone: 'warning', hollow: true },
  DRAFT: { label: 'Draft', tone: 'neutral', hollow: true },
  VOID: { label: 'Void', tone: 'neutral' },
  UNCOLLECTIBLE: { label: 'Unpaid', tone: 'danger' },
};

export function invoiceWord(status: string): StatusWord {
  return (
    INVOICE_WORD[status as InvoiceStatus] ?? {
      label: status,
      tone: 'neutral',
      hollow: true,
    }
  );
}

type TagTone = React.ComponentProps<typeof Tag>['tone'];

/** Metadata tags on a plan option (never status). */
export const PLAN_TAG: Record<
  'current' | 'scheduled' | 'custom',
  { label: string; tone: TagTone }
> = {
  current: { label: 'Current plan', tone: 'accent' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  custom: { label: 'Custom pricing', tone: 'neutral' },
};

// ---------------------------------------------------------------------------
// Adapters → PlanPricing
// ---------------------------------------------------------------------------

type AddonFields = Pick<
  Plan,
  | 'multilingual_addon_cents'
  | 'multilingual_included'
  | 'sms_ordering_addon_cents'
  | 'sms_ordering_included'
  | 'opentable_resy_addon_cents'
  | 'opentable_resy_included'
>;

function addonPricing(p: AddonFields): PlanPricing['addons'] {
  return {
    multilingual: {
      cents: p.multilingual_addon_cents ?? 0,
      included: !!p.multilingual_included,
    },
    sms_ordering: {
      cents: p.sms_ordering_addon_cents ?? 0,
      included: !!p.sms_ordering_included,
    },
    opentable_resy: {
      cents: p.opentable_resy_addon_cents ?? 0,
      included: !!p.opentable_resy_included,
    },
  };
}

export function pricingFromPlan(p: Plan): PlanPricing {
  return {
    currency: p.currency,
    baseCents: p.base_price_cents,
    includedMinutes: p.included_minutes,
    overageCentsPerMinute: p.overage_rate_cents_per_minute,
    setupFeeCents: p.setup_fee_cents ?? 0,
    perOrderCents: p.per_order_rate_cents ?? 0,
    trialDays: p.trial_period_days,
    addons: addonPricing(p),
  };
}

/** A subscription with custom pricing describes its own plan. */
export function pricingFromSubscription(s: Subscription): PlanPricing {
  return {
    currency: s.currency,
    baseCents: s.base_price_cents,
    includedMinutes: s.included_minutes,
    overageCentsPerMinute: s.overage_rate_cents_per_minute,
    setupFeeCents: s.setup_fee_cents ?? 0,
    perOrderCents: s.per_order_rate_cents ?? 0,
    addons: addonPricing(s),
  };
}

const ACTIVE_FIELD: Record<AddonKey, keyof Subscription> = {
  multilingual: 'multilingual_addon_active',
  sms_ordering: 'sms_ordering_addon_active',
  opentable_resy: 'opentable_resy_addon_active',
};

export function activeAddons(s: Subscription): AddonSelection {
  const included = addonPricing(s);
  return {
    multilingual:
      included.multilingual.included || !!s[ACTIVE_FIELD.multilingual],
    sms_ordering:
      included.sms_ordering.included || !!s[ACTIVE_FIELD.sms_ordering],
    opentable_resy:
      included.opentable_resy.included || !!s[ACTIVE_FIELD.opentable_resy],
  };
}

/** Add-ons on a subscription that are on (active or included). */
export function subscriptionAddonRows(s: Subscription) {
  const pricing = addonPricing(s);
  const active = activeAddons(s);
  return ADDON_KEYS.filter((k) => active[k]).map((k) => ({
    key: k,
    label: ADDON_META[k].label,
    included: pricing[k].included,
    cents: pricing[k].cents,
  }));
}

// ---------------------------------------------------------------------------
// Charges as receipt lines
// ---------------------------------------------------------------------------

export function storeChargeLines(u: UsageSummary): ReceiptLine[] {
  const cur = u.currency;
  const lines: ReceiptLine[] = [
    {
      key: 'base',
      label: 'Base plan fee',
      value: formatMoney(u.base_price_cents, cur, { exact: true }),
    },
    {
      key: 'overage',
      label: 'Overage',
      sub: `${formatMinutes(u.overage_minutes)} × ${formatRate(u.overage_rate_cents_per_minute, cur, 'min')}`,
      value: formatMoney(u.overage_cost_cents, cur, { exact: true }),
      muted: u.overage_minutes === 0,
    },
  ];
  if (u.subscription) {
    for (const a of subscriptionAddonRows(u.subscription)) {
      lines.push({
        key: a.key,
        label: `${a.label} add-on`,
        sub: a.included ? 'Included in plan' : undefined,
        value: formatMoney(a.included ? 0 : a.cents, cur, { exact: true }),
        muted: a.included,
      });
    }
  }
  return lines;
}

export function groupChargeLines(g: GroupUsageSummary): ReceiptLine[] {
  const cur = g.currency;
  return [
    {
      key: 'base',
      label: 'Base plan fee',
      value: formatMoney(g.base_price_cents, cur, { exact: true }),
    },
    {
      key: 'addons',
      label: 'Add-ons across stores',
      value: formatMoney(g.addon_cost_cents, cur, { exact: true }),
      muted: g.addon_cost_cents === 0,
    },
    {
      key: 'overage',
      label: 'Pooled overage',
      sub: `${formatMinutes(g.overage_minutes)} × ${formatRate(g.overage_rate_cents_per_minute, cur, 'min')}`,
      value: formatMoney(g.overage_cost_cents, cur, { exact: true }),
      muted: g.overage_minutes === 0,
    },
  ];
}

// ---------------------------------------------------------------------------
// Periods and dates
// ---------------------------------------------------------------------------

function safeZone(tz: string | undefined): string | undefined {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

export function formatDate(iso: string, tz?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(tz),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

/** "Jul 22, 2026 – Aug 21, 2026" — the API's end is exclusive, people's inclusive. */
export function periodLabel(
  p: Pick<BillingPeriod, 'period_start' | 'period_end'>,
  tz: string,
  compact = false,
): string {
  const start = new Date(p.period_start);
  const end = new Date(new Date(p.period_end).getTime() - 1);
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(tz),
    month: 'short',
    day: 'numeric',
    year: compact ? undefined : 'numeric',
  });
  return `${f.format(start)} – ${f.format(end)}`;
}

export function periodBarLabel(
  p: Pick<BillingPeriod, 'period_start'>,
  tz: string,
): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(tz),
    month: 'short',
  }).format(new Date(p.period_start));
}

export function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function localDateString(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

/**
 * Store-local day bounds for the daily chart. `period` is the whole cycle
 * (up to today when current); `week` is the last seven days of it.
 */
export function dailyRangeBounds(
  period: BillingPeriod,
  range: 'week' | 'period',
  timeZone: string,
  now: Date = new Date(),
): { start_date: string; end_date: string } {
  const periodStart = localDateString(new Date(period.period_start), timeZone);
  const periodEnd = localDateString(
    new Date(new Date(period.period_end).getTime() - 1),
    timeZone,
  );
  const endDate =
    period.is_current && now < new Date(period.period_end)
      ? localDateString(now, timeZone)
      : periodEnd;
  let startDate = periodStart;
  if (range === 'week') {
    const start = new Date(`${endDate}T00:00:00Z`);
    start.setUTCDate(start.getUTCDate() - 6);
    startDate = start.toISOString().slice(0, 10);
    if (startDate < periodStart) startDate = periodStart;
  }
  return { start_date: startDate, end_date: endDate };
}

export type Range = 'week' | 'period' | '6' | '12' | 'all';
export const STORE_RANGES: Range[] = ['week', 'period', '6', '12', 'all'];
export const GROUP_RANGES: Range[] = ['6', '12', 'all'];
export const RANGE_LABEL: Record<Range, string> = {
  week: 'Week',
  period: 'Period',
  '6': '6 months',
  '12': '12 months',
  all: 'All time',
};

export function rangeCount(range: Range, total: number): number {
  return range === '6' ? 6 : range === '12' ? 12 : total;
}

export function invoicePeriodLabel(inv: Invoice, tz: string): string {
  if (inv.period_start && inv.period_end)
    return periodLabel(
      { period_start: inv.period_start, period_end: inv.period_end },
      tz,
      true,
    );
  return formatDate(inv.created_at, tz);
}

export const INVOICES_PER_PAGE = 10;

export const BILLING_PATH = '/billing';
export const groupBillingPath = (groupId: number | string) =>
  `/billing/group/${groupId}`;
